import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Copy, ExternalLink } from "lucide-react";
import { SiClaude, SiGooglegemini, SiOpenai, SiPerplexity } from "react-icons/si";
import type { IconType } from "react-icons";
import { markFeatureTried } from "../lib/settings";

/* "Bring your own AI" — generates a grounded prompt the visitor pastes into
   their own assistant. More credible than our hosted answers (their model,
   their account), zero marginal cost, no quota. The corpus it points at is
   the public canon only (see scripts/build-corpus.mjs). */

const CORPUS_FULL_PATH = "/corpus/resume.md";
const CORPUS_COMPACT_PATH = "/corpus/resume-compact.md";
const FULL_URL = "https://resume.sjesdemos.com/corpus/resume.md";
const kb = (chars: number) => `${Math.round(chars / 1024)} KB`;

const PROVIDERS: { key: string; label: string; url: string; Icon: IconType; hue: string }[] = [
  { key: "claude", label: "Claude", url: "https://claude.ai/new", Icon: SiClaude, hue: "#d97757" },
  { key: "chatgpt", label: "ChatGPT", url: "https://chatgpt.com/", Icon: SiOpenai, hue: "#0a8f74" },
  { key: "gemini", label: "Gemini", url: "https://gemini.google.com/app", Icon: SiGooglegemini, hue: "#1c69ff" },
  { key: "perplexity", label: "Perplexity", url: "https://www.perplexity.ai/", Icon: SiPerplexity, hue: "#20808d" },
];

type CorpusKind = "online" | "compact" | "full";

function buildPrompt(question: string, inlineCorpus?: string): string {
  const q = question.trim() || "Give me a two-paragraph overview of Srinath's career and current focus.";
  const grounding = inlineCorpus
    ? `Use ONLY the résumé corpus between the markers below as your source.\n\n----- RÉSUMÉ CORPUS -----\n${inlineCorpus}\n----- END CORPUS -----`
    : `First fetch and read this résumé corpus in full: ${FULL_URL}\nUse ONLY that document as your source. If you cannot fetch URLs, tell me and I will paste the corpus instead.`;
  return [
    "You are answering questions about Srinath Jagannathan's professional career.",
    grounding,
    "Rules: ground every claim in the corpus and cite the experience or engagement title it came from; if the corpus does not cover something, say so plainly instead of guessing; do not speculate about anonymized client identities; keep the answer under 150 words.",
    `My question: ${q}`,
  ].join("\n\n");
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export function ByoAiRow({
  question,
  variant = "dock",
}: {
  question: string;
  variant?: "dock" | "inline";
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={variant === "inline" ? "byoai byoai--inline" : "byoai"}>
      <button
        type="button"
        className="byoai-toggle"
        onClick={() =>
          setOpen((v) => {
            if (!v) void markFeatureTried("byo-ai");
            return !v;
          })
        }
        aria-expanded={open}
      >
        <span className="byoai-label">Prefer your own AI?</span>
        <ChevronDown className={`size-[13px] byoai-caret ${open ? "is-open" : ""}`} />
      </button>
      {open && <ByoAiPanel question={question} />}
    </div>
  );
}

export function ByoAiPanel({ question, hideLead = false }: { question: string; hideLead?: boolean }) {
  const [copied, setCopied] = useState<CorpusKind | null>(null);
  const [provider, setProvider] = useState<(typeof PROVIDERS)[number]>(PROVIDERS[0]);
  // Corpus text is independent of the question, so fetch each once and cache it; sizes (character
  // counts, the thing paste limits constrain) drive the hints. Best-effort.
  const corpus = useRef<{ compact?: string; full?: string }>({});
  const [sizes, setSizes] = useState<{ compact?: number; full?: number }>({});

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [c, f] = await Promise.all([
          fetch(CORPUS_COMPACT_PATH).then((r) => r.text()),
          fetch(CORPUS_FULL_PATH).then((r) => r.text()),
        ]);
        if (!alive) return;
        corpus.current = { compact: c, full: f };
        setSizes({ compact: c.length, full: f.length });
      } catch {
        /* sizes stay unknown; copy still works via a fetch fallback */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const flash = (which: CorpusKind) => {
    setCopied(which);
    setTimeout(() => setCopied(null), 2400);
  };
  const copy = async (which: CorpusKind) => {
    let text: string;
    if (which === "online") {
      text = buildPrompt(question);
    } else {
      const cached = which === "compact" ? corpus.current.compact : corpus.current.full;
      const body =
        cached ??
        (await fetch(which === "compact" ? CORPUS_COMPACT_PATH : CORPUS_FULL_PATH)
          .then((r) => r.text())
          .catch(() => ""));
      text = body ? buildPrompt(question, body) : buildPrompt(question);
    }
    if (await copyText(text)) flash(which);
  };

  const options: { key: CorpusKind; label: string; hint: string }[] = [
    { key: "online", label: "Online corpus", hint: "your AI fetches it — needs web access" },
    { key: "compact", label: "Compact corpus", hint: sizes.compact ? `embedded · ${kb(sizes.compact)}` : "embedded" },
    { key: "full", label: "Full corpus", hint: sizes.full ? `embedded · ${kb(sizes.full)}` : "embedded" },
  ];

  return (
    <div className="byoai-panel">
      {!hideLead && (
        <p className="byoai-lead">
          Don't take our AI's word for it — copy a grounded prompt and ask yours.
          It reads the same public résumé corpus this site is built from.
        </p>
      )}
      <div className="byoai-providers" role="radiogroup" aria-label="AI provider">
        {PROVIDERS.map((p) => {
          const active = provider.key === p.key;
          return (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={active}
              className={`byoai-prov ${active ? "active" : ""}`}
              onClick={() => setProvider(p)}
            >
              <p.Icon size={13} color={active ? p.hue : "currentColor"} aria-hidden />
              {p.label}
            </button>
          );
        })}
      </div>
      <div className="byoai-copy-group">
        <span className="byoai-copy-label mono">Copy prompt</span>
        {options.map((o) => (
          <button key={o.key} type="button" className="byoai-copy-row" onClick={() => copy(o.key)}>
            <span className="byoai-copy-name">
              {o.label}
              <span className="byoai-copy-hint mono">{o.hint}</span>
            </span>
            {copied === o.key ? <Check className="size-[13px]" /> : <Copy className="size-[13px] muted" />}
          </button>
        ))}
      </div>
      <a className="btn btn-sm byoai-open" href={provider.url} target="_blank" rel="noopener">
        <provider.Icon size={13} aria-hidden /> Open {provider.label} <ExternalLink className="size-[12px]" />
      </a>
      <p className="byoai-note mono">
        Your question goes to your AI provider, not to this site. Corpus:{" "}
        <a href={CORPUS_COMPACT_PATH} target="_blank" rel="noopener">compact</a>
        {" · "}
        <a href={CORPUS_FULL_PATH} target="_blank" rel="noopener">full</a>
      </p>
    </div>
  );
}

/* Static-mode "bring your own AI" — shown on the flat résumé when interactivity is off. */
export function ByoAiStatic() {
  return (
    <section className="byoai byoai--static">
      <h2 className="display" style={{ fontSize: 22, marginBottom: 6 }}>
        Ask your AI
      </h2>
      <p className="byoai-lead">
        Copy a grounded prompt to ask your AI about this resume. It reads the same public resume
        corpus this site is built from
      </p>
      <ByoAiPanel question="" hideLead />
    </section>
  );
}
